import { Injectable } from '@angular/core';
import { Booking, BookingValidation, BookingSummary, BookingPhotoUpload, BookingImageType, BookingCancellation } from 'models';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class BookingService {
	constructor(private http: HttpClient) {}

	bookingPageSize(): number {
		return 20;
	}

	get(accommodationId: number): Observable<Booking[]> {
		return this.http.get<Booking[]>(`api/bookings/get/${accommodationId}`);
	}

	getSummary(): Observable<BookingSummary> {
		return this.http.get<BookingSummary>('api/bookings/summary/');
	}

	getSevenDaySummary(): Observable<BookingSummary> {
		return this.http.get<BookingSummary>('api/bookings/sevendaysummary/');
	}

	getPaged(pageIndex: number, pageSize: number, searchString: string): Observable<BookingSummary> {
		return this.http.get<BookingSummary>(`api/bookings/paged/${pageIndex}/${pageSize}?searchString=${searchString}`);
	}

	cancellation(bookingCancellation: BookingCancellation): Observable<void> {
		return this.http.post<void>(`api/bookings/cancellation/`, bookingCancellation);
	}

	cancellationAll(): Observable<void> {
		return this.http.get<void>(`api/bookings/cancelAll/`);
	}

	delete(bookingId: number): Observable<Booking[]> {
		return this.http.delete<Booking[]>(`api/bookings/delete`, {
			params: new HttpParams({
				fromObject: {
					bookingId: bookingId.toString(),
				},
			}),
		});
	}

	create(booking: Booking): Observable<Booking[]> {
		return this.http.post<Booking[]>('api/bookings/create', booking);
	}

	saveFakeBooking(bookingJson: string, clientId: string, source: string) {
		const headers = new HttpHeaders().set('Client-Id', clientId).append('Source', source);
		return this.http.post('api/fakebookings/createfakeBooking', bookingJson, { headers });
	}

	processFakeBookings(clientId: string) {
		return this.http.post('api/fakebookings/processfakebookings', clientId);
	}

	edit(booking: Booking): Observable<Booking[]> {
		return this.http.put<Booking[]>('api/bookings/edit', booking);
	}

	priceCheck(booking: Booking): Observable<BookingValidation> {
		return this.http.post<BookingValidation>('api/bookings/price-check', booking);
	}

	unsubscribeBooking(bid: number) {
		return this.http.post('api/bookings/unsubscribebooking', bid);
	}

	uploadBookingPhotos(bookingPhoto: BookingPhotoUpload, files: File[]): Observable<void> {
		const formData = new FormData();

		for (const file of files) {
			formData.append('file', file, file.name);
		}

		formData.append('bookingPhotoUpload', JSON.stringify(bookingPhoto));

		// change the default content type which is json to allow file upload
		return this.http.post<any>('api/bookings/uploadbookingphotos', formData);
	}

	getBookingPhotos(bookingImageType: BookingImageType, bookingId: number): Observable<string[]> {
		return this.http.get<string[]>(`api/bookings/getbookingphotos/${bookingImageType}/${bookingId}`);
	}
}
